import { Colors } from "@/enums/Colors";
import { KeyboardArrowDown } from "@mui/icons-material";
import { Box, ClickAwayListener, Portal } from "@mui/material";
import { useEffect, useRef, useState } from "react";

interface DropdownMenuProps {
  label: React.ReactNode;
  children: React.ReactNode;
  isOpen: boolean;
  onToggle: () => void;
}

export function DropdownMenu({ label, children, isOpen, onToggle }: DropdownMenuProps) {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

  const updatePosition = () => {
    if (anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom,
        left: rect.left,
        width: rect.width,
      });
    }
  };

  useEffect(() => {
    if (isOpen) {
      updatePosition();

      // Update position on scroll and resize
      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);

      // Use ResizeObserver to track anchor element size changes
      const resizeObserver = new ResizeObserver(() => {
        updatePosition();
      });

      if (anchorRef.current) {
        resizeObserver.observe(anchorRef.current);
      }

      return () => {
        window.removeEventListener("scroll", updatePosition, true);
        window.removeEventListener("resize", updatePosition);
        resizeObserver.disconnect();
      };
    }
  }, [isOpen]);

  const handleClickAway = () => {
    if (isOpen) {
      onToggle();
    }
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box sx={{ position: "relative" }}>
        <Box
          ref={anchorRef}
          onClick={onToggle}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            border: `1px solid ${Colors.PURPLE_6750A4}`,
            borderRadius: isOpen ? "0.5rem 0.5rem 0 0" : "0.5rem",
            cursor: "pointer",
            bgcolor: "#fff",
            p: "0.3rem",
            gap: "0.5rem",
            userSelect: "none",
            "&:hover": {
              backgroundColor: "rgba(120,84,196,0.05)",
            },
          }}
        >
          {label}

          <KeyboardArrowDown
            sx={{
              color: Colors.PURPLE_6750A4,
              transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s",
              mx: "0.5rem",
            }}
          />
        </Box>

        {isOpen && (
          <Portal>
            <Box
              onClick={(e) => {
                e.stopPropagation();
              }}
              sx={{
                position: "fixed",
                top: dropdownPosition.top,
                left: dropdownPosition.left,
                width: dropdownPosition.width,
                border: `1px solid ${Colors.PURPLE_6750A4}`,
                borderTop: "none",
                borderRadius: "0 0 0.5rem 0.5rem",
                bgcolor: "#fff",
                zIndex: 9999,
                boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
              }}
            >
              {children}
            </Box>
          </Portal>
        )}
      </Box>
    </ClickAwayListener>
  );
}
